import { action, computed, observable } from "mobx";
import moment from "moment";
import { nano } from "@bonliva-crm/common";
import { store } from "@store/Store";
import {
  ContactListItemFragment,
  GDPRConsentType,
  MassContactActions_Delete,
  MassContactActions_exportCSV,
  MassContactActions_SendEmail,
  MassContactActions_SendSMS,
  MassContactActions_SMSResultFragment as SMSResult,
  MassContactActions_UpdateResponsiblePerson,
  NodeType
} from "@graphql";
import locale from "@bonliva-crm/locale";
import { downloadFile } from "@api";

type MassContactActionsData = {
  items: ContactListItemFragment[];
  total?: number;
};

export type MassContactActionsStoreOptions = {
  data?: MassContactActionsData;
  getData?: () => Promise<MassContactActionsData>;
  nodeType: NodeType;
  allSelected: boolean;
  skipCreateMailAction?: boolean;
};

type CheckState = "None" | "Mine" | "All" | "Unknown";

export class MassContactActionsStore {
  @observable smsSending = false;
  @observable sendSmsResult: SMSResult["result"] = [];
  @observable isWorkRequestMailing = true;
  /** When true, an email send is a "Behovskampanj" (campaign) gated by the
   * JobMailingCampaignEmail consent; otherwise a "Behovsutskick" (direct request). */
  @observable isCampaign = false;
  @observable private _isLoading = true;
  @observable private _isDownloading = false;
  @observable private _isOpen = false;
  @observable private _onlyMy = true;
  @observable private _items: ContactListItemFragment[] = [];
  @observable private _selectedIds: string[] = [];
  @observable private _textEditorMode: "sms" | "email" = "sms";
  @observable private _isTextEditorOpen = false;
  @observable private _textEditorTo: string;
  @observable private _textEditorFrom = "Bonliva AB";
  /** How many items for a given request exist irrespective of mass list limit (1000) */
  @observable private _total: number | null = null;

  private _nodeType: NodeType;

  constructor(public options: MassContactActionsStoreOptions) {
    this._isOpen = true;
    this._nodeType = options.nodeType;
    this.isWorkRequestMailing = true;

    if (options.data) {
      this.processData(options.data);
    }

    if (options.getData) {
      options.getData().then((data) => this.processData(data));
    }

    if (!options.data && !options.getData) {
      throw new Error("missing data prop");
    }

    if (options.allSelected) {
      this.selectAll();
    }

    (window as any).massContactActionStore = this;
  }

  processData = (data: MassContactActionsData) => {
    this._items = data.items;
    this._total = typeof data.total === "number" ? data.total : null;
    this._isLoading = false;
  };

  @computed get checkState(): CheckState {
    if (this.selectedIds.length === 0) {
      return "None";
    }

    if (
      this.selectedItems.filter((x) => !this.isMine(x)).length === 0 &&
      this.selectedItems.length === this.allMySelectableItems.length
    ) {
      return this.hasFreeSelectableItems ? "Mine" : "All";
    }

    if (this.selectedItems.length === this.allSelectableItems.length) {
      return "All";
    }

    return "Unknown";
  }

  @computed get nextState(): CheckState {
    if (this.checkState === "None") {
      return "Mine";
    } else if (this.checkState === "Mine") {
      return "All";
    } else if (this.checkState === "All") {
      return "None";
    }

    return "Unknown";
  }

  @computed get items() {
    return this._items.sortBy((x) => x.Name);
  }

  @computed get itemsWithUnavailable() {
    return this.items.slice().map((item) => ({
      ...item,
      Unavailable: this.isItemUnavailable(item)
    }));
  }

  get total() {
    return this._total;
  }

  get isOpen() {
    return this._isOpen;
  }

  @computed get selectedIds() {
    let res = this._selectedIds;
    if (this.isWorkRequestMailing) {
      res = res.filter(this.isWorkRequestMailingAllowed);
    }
    return res;
  }

  get allSelected() {
    if (this._selectedIds.length === 0) {
      return false;
    }

    return (
      this._items.filter((i) => !this.isItemUnavailable(i)).length ===
      this._selectedIds.length
    );
  }

  get anySelected() {
    return this._selectedIds.length > 0;
  }

  get canSMS() {
    const { user } = store();
    return Boolean(user.myProfile.CellPhone);
  }

  // get canChangeResponsiblePerson() {
  //   const { user } = store();
  //   return user.hasPermission("IsConsultantsAdmin");
  // }

  get canDeleteConsultants() {
    const { user } = store();
    return user.can("consultant:delete:all");
  }

  get selectedItems() {
    if (this._selectedIds.length === 0) {
      return [];
    }

    return this._items.filter((item) => this.selectedIds.includes(item._id));
  }

  get isTextEditorOpen() {
    return this._isTextEditorOpen;
  }

  get textEditorInitialValues() {
    return {
      from: this._textEditorFrom,
      to: this._textEditorTo,
      message: ""
    };
  }

  get mode() {
    return this._textEditorMode;
  }

  get isLoading() {
    return this._isLoading;
  }

  get isDownloading() {
    return this._isDownloading;
  }

  get onlyMy() {
    return false;
  }

  set onlyMy(val: boolean) {
    this._onlyMy = val;

    if (this._onlyMy) {
      this._selectedIds = this._selectedIds.filter((id) => !this.isItemUnavailable(id));
    }
  }

  get allSelectableItems() {
    return this._items.filter(this.isSelectable);
  }

  get allMySelectableItems() {
    return this._items.filter((x) => this.isMine(x));
  }

  get hasSelectableItems() {
    return this.allSelectableItems.length > 0;
  }

  get hasFreeSelectableItems() {
    return (
      this.allSelectableItems.filter((x) => !this.selectedIds.includes(x._id)).length > 0
    );
  }

  @computed get onlyMyDisabled() {
    const { user } = store();
    return !user.myProfile.Permissions.CanSelectAllInMassActions;
  }

  @computed get isExcelDownloadEnabled() {
    return this.anySelected && !this.isDownloading;
  }

  @action.bound showLoader() {
    this._isLoading = true;
    this._isOpen = true;
  }

  @computed get unassigned() {
    return this._items.filter(this.isUnassigned).map((x) => x._id);
  }

  @computed get isSelectedOnlyUnassigned() {
    return this._selectedIds.every((x) => this.unassigned.includes(x));
  }

  /**
   * Not all items are visible due to the limit of 1000 items
   */
  @computed get isTruncated() {
    return this._total != null ? this.items.length < this._total : false;
  }

  @action.bound close() {
    this._items = [];
    this._selectedIds = [];
    this._isOpen = false;
    this.sendSmsResult = [];
  }

  @action.bound toggleItem(id: string) {
    this._selectedIds.toggle(id);
  }

  @action selectNone = () => {
    this._selectedIds = [];
  };

  @action selectMy = () => {
    this._selectedIds = this._items.filter(this.isMine).map((x) => x._id);
  };

  @action selectUnassigned = () => {
    this._selectedIds = this.unassigned;
  };

  @action selectMyAndUnassigned = () => {
    this._selectedIds = this._items
      .filter((id) => this.isMine(id) || this.isUnassigned(id))
      .map((x) => x._id);
  };

  @action selectAll = () => {
    this._selectedIds = this._items.filter(this.isSelectable).map((x) => x._id);
  };

  @action toggleAll = () =>
    this._selectedIds.length > 0 ? this.selectNone() : this.selectAll();

  @action.bound mailSelected() {
    if (this.selectedItems.length === 0) {
      return;
    }

    const items = this.selectedItems.filter((x) => x.EmailValid);

    store().emailEditor.requestEmailEditor(
      {
        body: "",
        subject: "",
        to: items.map((item) => ({
          name: item.Name,
          email: item.Email,
          avatarPic: item.AvatarPic
        })),
        attachments: []
      },
      (emailData) => {
        if (!emailData) {
          // user has cancelled the editor
          return Promise.resolve();
        }

        return MassContactActions_SendEmail.func({
          body: emailData.body,
          subject: emailData.subject,
          nodeType: this._nodeType,
          nodeIds: items.map((x) => x._id),
          attachment: emailData.attachments?.map((x) => x.FileRef.NodeID),
          campaign: this.isCampaign
        })
          .then(() => {
            store().emailEditor.close();
            store().snackbars.success("Email sent successfully ");
          })
          .catch((err) => {
            store().snackbars.error(`Cannot send email\nReason: ${err}`);
          });
      },
      { withAttachments: true }
    );
  }

  @action.bound textEditorSubmit({ message }: { message: string }) {
    if (this._textEditorMode === "sms") {
      return this.sendSMS(message);
    } else {
      throw new Error("not implemented");
    }
  }

  @action.bound openSMSDialog() {
    const { user } = store();

    if (this.selectedItems.length === 0) {
      return;
    }

    this._textEditorFrom = user.myProfile.CellPhone;
    this._isTextEditorOpen = true;
    this._textEditorTo = this.selectedItems
      .filter((item) => item.CellPhone)
      .map((item) => `${item.Name} (${item.CellPhone})`)
      .join(", ");
  }

  @action.bound sendSMS(message: string) {
    if (this.selectedItems.length === 0) {
      return Promise.reject(new Error("empty list"));
    }

    if (!message) {
      return Promise.reject(new Error("empty message"));
    }
    this.sendSmsResult = [];

    return MassContactActions_SendSMS.func({
      message,
      consultantIds: this.selectedItems
        .filter((item) => item.CellPhoneValid)
        .map((item) => item._id)
    }).then(({ data }) => {
      this.sendSmsResult = data.sendMassSMSToConsultants.result;
      store().snackbars.info(locale["SMS messages in progress"]);
      this.closeTextEditor();
    });
  }

  @action.bound
  async updateResponsiblePerson() {
    if (this.selectedIds.length === 0) {
      return Promise.reject(new Error("empty list"));
    }

    const { snackbars, ui } = store();

    const user = await ui.selectUser({ includeEmpty: true });

    MassContactActions_UpdateResponsiblePerson.func({
      ids: this.selectedIds,
      responsiblePersonId: user ? user._id : null
    })
      .then(({ data: { bulkUpdateResponsiblePerson: result } }) => {
        snackbars.success(`Updated ${result.updated}`);

        this.close();
      })
      .catch((e) => snackbars.error(`Failed\nResult: ${e}`));
  }

  @action.bound
  async deleteItems() {
    const { snackbars, ui } = store();

    ui.confirmDialog
      .confirm<"ok" | "cancel">((options) => {
        options.title = locale.AreYouSure;

        options.actions.push({
          title: locale.Cancel,
          type: "regular",
          value: "cancel"
        });

        options.actions.push({
          title: locale.OK,
          type: "primary",
          value: "ok"
        });
      })
      .then(async (result) => {
        if (result !== "ok") {
          return;
        }

        try {
          await MassContactActions_Delete.func({ ids: this.selectedIds });
          snackbars.success(locale.OK);
          this.close();
        } catch (e) {
          console.error(e);

          snackbars.error(e.graphQLErrors ? e.graphQLErrors[0].message : e.toString());
        }
      });
  }

  @action.bound closeTextEditor() {
    this._isTextEditorOpen = false;
  }

  @action.bound getCsv() {
    this._isDownloading = true;

    console.log(this._isDownloading);
    MassContactActions_exportCSV.func(
      {
        ids: this.selectedIds
      },
      { fetchPolicy: "network-only" }
    )
      .then(({ data }) => {
        return downloadFile(data.consultantsCSV.URL, data.consultantsCSV.Filename);
      })
      .catch((e) => {
        store().snackbars.error(`Cannot download file\nReason: ${e}`);
      })
      .finally(() => {
        this._isDownloading = false;
      });
  }

  @action setWorkRequestMailing = (v: boolean) => {
    this.isWorkRequestMailing = v;
  };

  @action setCampaign = (v: boolean) => {
    this.isCampaign = v;
  };

  private getItemById(id: string) {
    return this.items.find((item) => item._id === id);
  }

  private isMailingConsentAllowed(item: ContactListItemFragment): boolean {
    const records = item.GDPRConsentRecords ?? [];

    if (this.isCampaign) {
      const campaignConsent = records.find(
        (consent) => consent.type === GDPRConsentType.JobMailingCampaignEmail
      );
      if (!campaignConsent) return true;
      return Boolean(campaignConsent.granted);
    }

    const emailConsent = records.find(
      (consent) => consent.type === GDPRConsentType.JobMailingEmail
    );
    const smsConsent = records.find(
      (consent) => consent.type === GDPRConsentType.JobMailingSms
    );

    if (!emailConsent && !smsConsent) return true;
    return Boolean(emailConsent?.granted) || Boolean(smsConsent?.granted);
  }

  isItemUnavailable = (item: ContactListItemFragment | string): boolean => {
    const { user } = store();

    const itemRef = typeof item === "string" ? this.getItemById(item) : item;

    if (this.isWorkRequestMailing && !this.isMailingConsentAllowed(itemRef)) {
      return true;
    }

    if (!this.onlyMy) {
      return false;
    }

    return itemRef.ResponsiblePerson && itemRef.ResponsiblePerson.PersonId !== user.id;
  };

  isWorkRequestMailingAllowed = (itemId: string) => {
    const item = this.getItemById(itemId);
    return this.isMailingConsentAllowed(item);
  };

  @action.bound parseTemplate(template: string) {
    const { user } = store();

    const vars = {
      sender: user.myProfile.Name,
      date: moment.utc().format("YYYY-MM-DD"),
      time: moment.utc().format("hh-mm")
    };

    return nano(template, vars);
  }

  private isSelectable = (item: ContactListItemFragment) => {
    if (this.isWorkRequestMailing && !this.isMailingConsentAllowed(item)) {
      return false;
    }

    if (!this.onlyMy) {
      return true;
    }

    return !item.ResponsiblePerson || item.ResponsiblePerson.PersonId === store().user.id;
  };

  private isMine = (item: ContactListItemFragment) => {
    return item.ResponsiblePerson && item.ResponsiblePerson.PersonId === store().user.id;
  };

  private isUnassigned = (item: ContactListItemFragment) => {
    return !item.ResponsiblePerson;
  };
}
